/**
 * Détection du profil via BT-24 (Specification identifier / CustomizationID).
 *
 * Les URN réellement rencontrés dans la nature ne sont pas toujours
 * canoniques : séparateurs `:` au lieu de `#`, versions XRechnung 1.2 → 3.0,
 * URN ZUGFeRD 2.0… On classe donc par famille puis par suffixe, et on
 * conserve l'URN brut pour l'affichage (tooltip du badge).
 */
import type { InvoiceProfile, ProfileFamily, ProfileInfo } from './model';

interface Rule {
  family: ProfileFamily;
  profile: InvoiceProfile;
  match: 'exact' | 'prefix';
}

/** Correspondances exactes connues (référence : Factur-X 1.0.07, XRechnung 3.0). */
const EXACT: Record<string, Rule> = {
  'urn:factur-x.eu:1p0:minimum': { family: 'facturx', profile: 'MINIMUM', match: 'exact' },
  'urn:cen.eu:en16931:2017#compliant#urn:factur-x.eu:1p0:basicwl': { family: 'facturx', profile: 'BASIC_WL', match: 'exact' },
  'urn:cen.eu:en16931:2017#compliant#urn:factur-x.eu:1p0:basic': { family: 'facturx', profile: 'BASIC', match: 'exact' },
  'urn:cen.eu:en16931:2017': { family: 'en16931', profile: 'EN16931', match: 'exact' },
  'urn:cen.eu:en16931:2017#conformant#urn:factur-x.eu:1p0:extended': { family: 'facturx', profile: 'EXTENDED', match: 'exact' },
  'urn:zugferd.de:2p0:minimum': { family: 'zugferd2', profile: 'MINIMUM', match: 'exact' },
  'urn:zugferd.de:2p0:basicwl': { family: 'zugferd2', profile: 'BASIC_WL', match: 'exact' },
  'urn:zugferd.de:2p0:basic': { family: 'zugferd2', profile: 'BASIC', match: 'exact' },
  'urn:zugferd.de:2p0:en16931': { family: 'zugferd2', profile: 'EN16931', match: 'exact' },
  'urn:zugferd.de:2p0:extended': { family: 'zugferd2', profile: 'EXTENDED', match: 'exact' },
};

const XRECHNUNG_PREFIXES = [
  'urn:cen.eu:en16931:2017#compliant#urn:xeinkauf.de:kosit:xrechnung',
  'urn:cen.eu:en16931:2017#compliant#urn:xoev-de:kosit:standard:xrechnung',
];

export function detectProfile(rawUrn: string | null | undefined): ProfileInfo {
  const urn = (rawUrn ?? '').trim();
  if (!urn) {
    return {
      profile: 'UNKNOWN',
      urn: null,
      family: 'unknown',
      version: null,
      match: 'none',
      notes: ['BT-24 absent'],
    };
  }

  const lower = urn.toLowerCase();
  const exact = EXACT[lower];
  if (exact) {
    const version = exact.family === 'facturx' ? '1.0' : exact.family === 'zugferd2' ? '2.0' : null;
    return { ...exact, urn, version, notes: [] };
  }

  // ZUGFeRD 1.0 (urn:ferd:CrossIndustryDocument:invoice:1p0:…) : format obsolète.
  if (lower.startsWith('urn:ferd:') || lower.includes('crossindustrydocument:invoice:1p0')) {
    return {
      profile: 'UNKNOWN',
      urn,
      family: 'zugferd1',
      version: '1.0',
      match: 'legacy',
      notes: ['ZUGFeRD 1.0 : syntaxe obsolète, non couverte par EN 16931'],
    };
  }

  if (lower.includes('xrechnung')) {
    const v = /xrechnung[_:](\d+)[._](\d+)/.exec(lower);
    const version = v ? `${v[1]}.${v[2]}` : null;
    const isCanonical = XRECHNUNG_PREFIXES.some((p) => lower.startsWith(p));
    return {
      profile: 'XRECHNUNG',
      urn,
      family: 'xrechnung',
      version,
      match: isCanonical ? 'exact' : 'prefix',
      notes: isCanonical ? [] : ['URN XRechnung non canonique mais identifiable'],
    };
  }

  if (lower.includes('peppol')) {
    return {
      profile: 'EN16931',
      urn,
      family: 'peppol',
      version: /billing:(\d+\.\d+)/.exec(lower)?.[1] ?? null,
      match: 'prefix',
      notes: ['Profil Peppol BIS Billing (CIUS EN 16931)'],
    };
  }

  // Factur-X / ZUGFeRD 2 : on classe par suffixe, du plus spécifique au plus général.
  const isFacturX = lower.includes('factur-x.eu:1p0:');
  const isZugferd2 = lower.includes('zugferd.de:2p0:');
  if (isFacturX || isZugferd2) {
    const family: ProfileFamily = isFacturX ? 'facturx' : 'zugferd2';
    const version = isFacturX ? '1.0' : '2.0';
    let profile: InvoiceProfile = 'UNKNOWN';
    if (lower.includes(':minimum')) profile = 'MINIMUM';
    else if (lower.includes('basicwl') || lower.includes('basic-wl')) profile = 'BASIC_WL';
    else if (lower.includes(':basic') || lower.endsWith('basic')) profile = 'BASIC';
    else if (lower.includes('extended')) profile = 'EXTENDED';
    else if (lower.includes('en16931')) profile = 'EN16931';
    return {
      profile,
      urn,
      family,
      version,
      match: profile === 'UNKNOWN' ? 'none' : 'prefix',
      notes: profile === 'UNKNOWN' ? ['Suffixe de profil non reconnu'] : [],
    };
  }

  if (lower.includes('en16931')) {
    return {
      profile: 'EN16931',
      urn,
      family: 'en16931',
      version: '2017',
      match: 'prefix',
      notes: ['URN EN 16931 avec extension non répertoriée'],
    };
  }

  if (lower.includes('order-x')) {
    return { profile: 'UNKNOWN', urn, family: 'unknown', version: null, match: 'none', notes: ['ORDER-X : commande, pas une facture'] };
  }

  return { profile: 'UNKNOWN', urn, family: 'unknown', version: null, match: 'none', notes: ['URN non répertorié'] };
}

/** Niveaux XMP (`fx:ConformanceLevel`) → profil attendu, pour recoupement. */
const XMP_LEVELS: Record<string, InvoiceProfile> = {
  minimum: 'MINIMUM',
  'basic wl': 'BASIC_WL',
  'basic-wl': 'BASIC_WL',
  basic: 'BASIC',
  'en 16931': 'EN16931',
  en16931: 'EN16931',
  extended: 'EXTENDED',
};

export function profileFromXmpLevel(level: string | null | undefined): InvoiceProfile | null {
  if (!level) return null;
  return XMP_LEVELS[level.trim().toLowerCase()] ?? null;
}
